import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { DialogDataSegment, Segment, SegmentFormData } from '@core/interfaces';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FormValidator } from '@validators/linked-forms-no-all-options-validation';
import { sentry } from 'sentry';

import { SegmentService } from '../services/segment.service';
import { DialogSegmentsMapper } from './dialog-segments.mapper';
import { TabSettingsComponent } from './tabs/settings/tab-settings.component';

@Component({
  selector: 'dialog-segments',
  templateUrl: './dialog-segments.component.html',
  styleUrls: ['./dialog-segments.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogSegmentsComponent implements OnInit {
  public generalForm: FormGroup;
  public maintenanceTypesForm: FormGroup;
  public segmentMaintenanceFormsArray: FormArray;

  public segment: Segment;
  public selectedTabIndex: number;
  public segmentsSelection: string[];
  public defaultValues: SegmentFormData;

  public isSaving: boolean;
  public isViewMode: boolean;
  public isDuplicatedForm: boolean;
  public noMaintenancesSelected: boolean;
  public hasEditGroupPermission: boolean;

  @ViewChild('settingSlider', { static: true })
  private settingsTab: TabSettingsComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: DialogDataSegment,
    private readonly matDialogRef: MatDialogRef<DialogSegmentsComponent>,
    private readonly authenticationService: AuthenticationService,
    private readonly customMessageService: CustomMessagesService,
    private readonly progressBar: FuseProgressBarService,
    private readonly segmentService: SegmentService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.isSaving = false;
    this.isDuplicatedForm = false;
    this.hasEditGroupPermission = false;

    this.selectedTabIndex = 0;
    this.segment = this.dialogData.segment;
    this.isViewMode = this.dialogData.isViewMode;
    this.hasEditGroupPermission = this.authenticationService.hasPermission('groups-edit');
  }

  public ngOnInit(): void {
    this.generalForm = DialogSegmentsMapper.toGeneralForm(this.segment, this.formBuilder);
    this.maintenanceTypesForm = DialogSegmentsMapper.toMaintenanceTypesForm(this.segment, this.formBuilder);
    this.segmentMaintenanceFormsArray = DialogSegmentsMapper.toSegmentMaintenanceFormsArray(this.segment, this.formBuilder);

    this.defaultValues = {
      general: this.generalForm.value.name,
      maintenanceForms: this.segmentMaintenanceFormsArray.value
    };

    this.segmentMaintenanceFormsArray.setValidators(FormValidator.validateForm);
    this.segmentMaintenanceFormsArray.valueChanges.subscribe(() => {
      this.isDuplicatedForm = this.segmentMaintenanceFormsArray.controls.some((formGroup) => formGroup.get('form').hasError('formDuplicated'));
    });

    this.matDialogRef.disableClose = true;
    this.matDialogRef.backdropClick().subscribe(() => {
      this.matDialogRef.close();
    });

    if (this.isViewMode) {
      this.generalForm.disable();
    }
  }

  public onLinkMaintenanceTypesToSegmentChange(value: boolean): void {
    this.noMaintenancesSelected = value;
  }

  public async saveSegment({ closeDialog }: { closeDialog: boolean }): Promise<boolean> {
    this.isSaving = true;
    this.progressBar.show();

    let savedSuccessfully = false;

    try {
      const input = DialogSegmentsMapper.toSaveSegmentInput({
        segment: this.segment,
        generalForm: this.generalForm,
        settingsTab: this.settingsTab,
        segmentsIds: this.segmentsSelection,
        maintenanceTypesForm: this.maintenanceTypesForm,
        segmentMaintenanceFormsArray: this.segmentMaintenanceFormsArray,
      });
      this.segment = await this.segmentService.save(this.segment.id, input);
      this.customMessageService.success(`SEGMENTS.SUCCESS_MESSAGE`);
      savedSuccessfully = true;

      if (closeDialog) {
        this.matDialogRef.close(true);
        this.customMessageService.success('SEGMENTS.SUCCESS_MESSAGE');
      }
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'segment_name_duplicated') {
        this.generalForm.get('name').setErrors({
          'segment_name_duplicated': true
        });

        return;
      }

      if (code === 'segment_maintenance_type_form_duplicated') {
        this.generalForm.get('name').setErrors({
          'formDuplicated': true
        });

        return;
      }

      this.customMessageService.error('SEGMENTS.ERROR_MESSAGE');
      sentry.captureException(err);
    } finally {
      this.progressBar.hide();
      this.isSaving = false;
    }
    return savedSuccessfully;
  }

  public async archiveOrUnarchiveSegment(archived: boolean): Promise<void> {
    this.segment.archived = archived;
    await this.saveSegment({ closeDialog: true });
  }

  async continue(): Promise<void> {
    if (this.selectedTabIndex !== 0) {
      this.selectedTabIndex++;
      return;
    }

    const savedSuccessfully = await this.saveSegment({ closeDialog: false });

    if (savedSuccessfully) {
      this.selectedTabIndex = 1;
    }
  }
}
